import {
  QrcodeOutlined,
  EditOutlined,
  PaperClipOutlined,
  CheckOutlined,
  CloseOutlined,
  StopOutlined,
  PrinterOutlined,
} from '@ant-design/icons'
import ProfileDrawer from '@/components/shared/ProfileDrawer'
import { IconButton, Avatar, Tabs, message, Button, AttendeeStatusBadge, ActivityLogCard } from '@/components/ui'
import type { Attendee, RegistrationField } from '../types'
import { formatFieldValue, formatDateTime, formatPhoneNumber } from '../utils/formatters'

interface AttendeeProfileDrawerProps {
  open: boolean
  onClose: () => void
  attendee: Attendee | null
  registrationFields: RegistrationField[]
  onEdit: () => void
  onPrintBadge?: (attendee: Attendee) => void
  isPrintEnabled?: boolean
  printDisabledReason?: string
  isPrintingBadge?: boolean
  onApprove?: (id: number) => void
  onReject?: (id: number) => void
  onCancel?: (id: number) => void
}

export default function AttendeeProfileDrawer({
  open,
  onClose,
  attendee,
  registrationFields,
  onEdit,
  onPrintBadge,
  isPrintEnabled = true,
  printDisabledReason,
  isPrintingBadge = false,
  onApprove,
  onReject,
  onCancel,
}: AttendeeProfileDrawerProps) {
  if (!attendee) return null

  const canApprove = attendee.status !== 'approved'
  const canReject = attendee.status === 'pending'
  const canCancel = attendee.status !== 'cancelled'

  const sessionChoiceFields = registrationFields.filter((f) => f.kind === 'session_choice')
  const fileUploads = attendee.file_uploads || []
  const phoneField = registrationFields.find(
    (field) => field.identifier === 'phone' || field.identifier === 'contact_number',
  )
  const rawPhoneValue = phoneField
    ? attendee.custom_fields?.[phoneField.identifier]
    : attendee.contact_number
  const phoneValue = phoneField
    ? phoneField.identifier === 'phone' || phoneField.identifier === 'contact_number'
      ? formatPhoneNumber(rawPhoneValue)
      : formatFieldValue(phoneField, rawPhoneValue)
    : formatPhoneNumber(rawPhoneValue)
  const hasPhone = Boolean(phoneValue && phoneValue !== '-')
  const attendeeDisplayName =
    attendee.full_name?.trim() || attendee.email || `Registrant #${attendee.id}`

  return (
    <ProfileDrawer title="Attendee Profile" onClose={onClose} open={open}>
      {/* Profile Card */}
      <div className="bg-gray-100 rounded-lg p-6 mb-3">
        <div className="flex items-start justify-between mb-3">
          {/* Left side: Avatar + name + status badge */}
          <div className="flex items-center gap-3">
            <Avatar
              name={attendeeDisplayName}
              src={attendee.profile_photo_url || undefined}
              size="large"
              className="w-14 h-14 text-2xl"
            />
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h2 className="font-semibold">{attendeeDisplayName}</h2>
                <AttendeeStatusBadge status={attendee.status} />
              </div>
              <p className="text-sm text-gray-500">{attendee.email}</p>
              {hasPhone && <p className="text-sm text-gray-500">{phoneValue}</p>}
            </div>
          </div>

          {/* Right side: Icon buttons */}
          <div className="flex gap-1">
            <IconButton
              icon={<QrcodeOutlined />}
              disabled={!attendee.qr_code}
              onClick={() => {
                if (attendee.qr_code) {
                  navigator.clipboard.writeText(attendee.qr_code)
                  message.success('QR code copied!')
                } else {
                  message.warning('QR code not available for this attendee')
                }
              }}
              title={!attendee.qr_code ? 'QR code not assigned' : 'Copy QR code'}
              ariaLabel="Copy QR code"
            />
            <IconButton
              icon={<EditOutlined />}
              onClick={onEdit}
              ariaLabel="Edit attendee"
              title="Edit"
            />
            {onPrintBadge && (
              <IconButton
                icon={<PrinterOutlined />}
                onClick={() => onPrintBadge(attendee)}
                disabled={!isPrintEnabled || isPrintingBadge}
                ariaLabel="Queue badge print"
                title={
                  isPrintingBadge
                    ? 'Queueing print job...'
                    : isPrintEnabled
                      ? 'Queue badge print'
                      : printDisabledReason || 'Print unavailable'
                }
              />
            )}
            {canCancel && !canApprove && onCancel && (
              <IconButton
                icon={<StopOutlined />}
                onClick={() => onCancel(attendee.id)}
                ariaLabel="Cancel registration"
                title="Cancel registration"
              />
            )}
          </div>
        </div>

        {/* Status action buttons */}
        {(canApprove || canReject || canCancel) && (
          <div className="flex gap-2 mb-3">
            {canApprove && onApprove && (
              <Button
                variant="primary"
                size="small"
                icon={<CheckOutlined />}
                onClick={() => onApprove(attendee.id)}
              >
                Approve
              </Button>
            )}
            {canReject && onReject && (
              <Button
                variant="danger"
                size="small"
                icon={<CloseOutlined />}
                onClick={() => onReject(attendee.id)}
              >
                Reject
              </Button>
            )}
          </div>
        )}

        {/* Dynamic fields in single column layout */}
        <div className="flex flex-col gap-y-3">
          {/* Other fields */}
          {registrationFields
            .filter(
              (field) =>
                field.identifier !== 'email' &&
                field.identifier !== 'full_name' &&
                field.identifier !== 'phone' &&
                field.identifier !== 'contact_number',
            )
            .filter((field) => field.kind !== 'session_choice' && field.kind !== 'file_upload')
            .map((field) => {
              const value = attendee.custom_fields?.[field.identifier]
              const displayValue = formatFieldValue(field, value)

              return (
                <div key={field.id} className="flex flex-col gap-0.5">
                  <div className="text-xs text-gray-500">{field.label}</div>
                  <div className="text-sm font-semibold break-words">{displayValue}</div>
                </div>
              )
            })}
        </div>

        {/* Session Choices */}
        {sessionChoiceFields.length > 0 && (
          <div className="mt-3">
            <div className="space-y-4">
              {sessionChoiceFields.map((field) => {
                const selectedSessionId = attendee.custom_fields?.[field.identifier]
                const sessionOption = field.session_options?.find(
                  (opt) => opt.agenda_session_id.toString() === String(selectedSessionId),
                )
                const sessionLabel = sessionOption?.agenda_session?.name || 'No selection'
                const sessionMeta = sessionOption?.agenda_session
                  ? `${sessionOption.agenda_session.start_time || ''} ${sessionOption.agenda_session.end_time ? `- ${sessionOption.agenda_session.end_time}` : ''}${
                      sessionOption.agenda_session.venue
                        ? ` • ${sessionOption.agenda_session.venue}`
                        : ''
                    }`
                  : ''
                return (
                  <div key={field.id} className="flex flex-col gap-0.5">
                    <div className="text-xs text-gray-500">{field.label}</div>
                    <div className="flex-1">
                      <div className="text-sm font-semibold">{sessionLabel}</div>
                      {sessionMeta && <div className="text-xs text-gray-700">{sessionMeta}</div>}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* File Uploads */}
        {fileUploads.length > 0 && (
          <div className="mt-6">
            <h4 className="text-sm font-semibold mb-3">File Uploads</h4>
            <div className="space-y-3">
              {fileUploads.map((file) => (
                <div
                  key={file.id}
                  className="flex items-start justify-between border border-gray-200 rounded-md p-3 bg-white"
                >
                  <div>
                    <div className="text-sm font-medium flex items-center gap-2">
                      <PaperClipOutlined /> {file.field_label || file.field_identifier}
                    </div>
                    <div className="text-xs text-gray-500">
                      {file.filename} • {(file.byte_size / 1024 / 1024).toFixed(2)} MB •{' '}
                      {file.content_type}
                    </div>
                  </div>
                  <a
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-ava-blue-05 text-sm font-medium"
                  >
                    View
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs
        defaultActiveKey="general"
        size="small"
        items={[
          {
            key: 'general',
            label: 'General',
            children: (
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Registered At</div>
                  <div className="text-sm font-semibold">{formatDateTime(attendee.created_at)}</div>
                </div>

                {attendee.checked_in_at && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Checked-In At</div>
                    <div className="text-sm font-semibold">
                      {formatDateTime(attendee.checked_in_at)}
                    </div>
                  </div>
                )}
              </div>
            ),
          },
          {
            key: 'badge-print',
            label: 'Badge Print',
            children: (
              <div>
                <h4 className="text-sm font-semibold mb-2">Badge Print Jobs</h4>
                {attendee.badge_print_logs && attendee.badge_print_logs.length > 0 ? (
                  <div className="space-y-2">
                    {attendee.badge_print_logs.map((log) => (
                      <div key={log.id} className="rounded border border-gray-100 bg-white px-3 py-2">
                        <div className="text-xs font-medium text-gray-900">
                          {log.printer_name || 'Unknown printer'}
                        </div>
                        <div className="text-[11px] text-gray-500">
                          {log.status} • Queued by {log.triggered_by || 'System'} • {formatDateTime(log.printed_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">No badge print jobs yet</p>
                )}
              </div>
            ),
          },
          {
            key: 'scan-point',
            label: 'Scan Point',
            children: (
              <div>
                <h4 className="text-sm font-semibold mb-2">Scan Point Logs</h4>
                {attendee.scan_attendances && attendee.scan_attendances.length > 0 ? (
                  <div className="space-y-2">
                    {attendee.scan_attendances.map((scan) => (
                      <div key={scan.id} className="rounded border border-gray-100 bg-white px-3 py-2">
                        <div className="text-xs font-medium text-gray-900">{scan.scan_point_name}</div>
                        <div className="text-[11px] text-gray-500">
                          {scan.scanned_by ? `Scanned by ${scan.scanned_by} • ` : ''}
                          {formatDateTime(scan.scanned_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">No scan logs yet</p>
                )}
              </div>
            ),
          },

          {
            key: 'email',
            label: 'Email',
            children: (
              <div>
                {(() => {
                  const emailLogTypes = [
                    'email_blast',
                    'confirmation_email',
                    'acknowledgement_email',
                    'reminder',
                  ]
                  const emailLogs = attendee.communication_logs
                    ?.filter(log => emailLogTypes.includes(log.log_type)) || []

                  return emailLogs.length > 0 ? (
                    <div className="space-y-3">
                      <p className="text-sm font-medium mb-3">Activity log ({emailLogs.length})</p>
                      {emailLogs.map(log => (
                        <ActivityLogCard
                          key={log.id}
                          type="email"
                          title={log.subject || 'Email'}
                          message={log.message}
                          // createdAt={formatDateTime(log.created_at)}
                          sentAt={log.sent_at ? formatDateTime(log.sent_at) : undefined}
                        />
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">No emails sent yet</p>
                  )
                })()}
              </div>
            ),
          },
          {
            key: 'whatsapp',
            label: 'WhatsApp',
            children: (
              <div>
                {(() => {
                  const whatsappLogTypes = [
                    'whatsapp_blast',
                    'whatsapp_confirmation',
                    'whatsapp_acknowledgement',
                    'whatsapp_reminder',
                  ]
                  const whatsappLogs = attendee.communication_logs
                    ?.filter(log => whatsappLogTypes.includes(log.log_type)) || []

                  return whatsappLogs.length > 0 ? (
                    <div className="space-y-3">
                      <p className="text-sm font-medium mb-3">Activity log ({whatsappLogs.length})</p>
                      {whatsappLogs.map(log => (
                        <div key={log.id}>
                          <ActivityLogCard
                            type="whatsapp"
                            title={log.subject || 'WhatsApp Message'}
                            message={log.message}
                            sentAt={log.sent_at ? formatDateTime(log.sent_at) : undefined}
                          />
                          <div className="mt-1 ml-1 flex items-center gap-2">
                            <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${
                              log.status === 'sent' || log.status === 'delivered'
                                ? 'bg-green-50 text-green-600'
                                : log.status === 'failed'
                                  ? 'bg-red-50 text-red-600'
                                  : 'bg-gray-50 text-gray-500'
                            }`}>
                              {log.status?.toUpperCase() || 'UNKNOWN'}
                            </span>
                            {log.status === 'failed' && log.metadata?.error && (
                              <span className="text-[10px] text-red-400 truncate max-w-[200px]" title={log.metadata.error}>
                                {log.metadata.error}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">No WhatsApp messages sent yet</p>
                  )
                })()}
              </div>
            ),
          },
          // {
          //   key: 'push',
          //   label: 'Push Notification',
          //   children: (
          //     <div>
          //       {(() => {
          //         const pushLogs = attendee.communication_logs
          //           ?.filter(log => log.log_type === 'push_notification') || []

          //         // Sample logs for stakeholder demo
          //         const samplePushLogs = [
          //           {
          //             id: 'sample-push-1',
          //             subject: 'Welcome to the AAVII Worldwide Event!',
          //             created_at: '2025-07-01T09:00:00',
          //             sent_at: '2025-07-01T09:00:00',
          //           },
          //           {
          //             id: 'sample-push-2',
          //             subject: "We're almost there! AAVII Worldwide Event 2025 starts in 24 hours!",
          //             created_at: '2025-07-01T11:00:00',
          //             sent_at: '2025-07-01T11:30PM',
          //           },
          //           {
          //             id: 'sample-push-3',
          //             subject: 'Check the event schedule and join us for the grand opening!',
          //             created_at: '2025-07-01T12:00:00',
          //             sent_at: '2025-07-01T12:20PM',
          //           },
          //         ]
          //         const displayLogs = pushLogs.length > 0 ? pushLogs : samplePushLogs

          //         return (
          //           <div className="space-y-3">
          //             <p className="text-sm font-medium mb-3">Activity log ({displayLogs.length})</p>
          //             {displayLogs.map(log => (
          //               <ActivityLogCard
          //                 key={log.id}
          //                 type="push"
          //                 title={log.subject || 'Push Notification'}
          //                 createdAt={formatDateTime(log.created_at)}
          //                 sentAt={log.sent_at ? formatDateTime(log.sent_at) : undefined}
          //               />
          //             ))}
          //           </div>
          //         )
          //       })()}
          //     </div>
          //   ),
          // },
        ]}
      />
    </ProfileDrawer>
  )
}
